import { createAsyncThunk } from '@reduxjs/toolkit'
import * as tariffsApi from '../../api/tariffs.api'
import type { Tariff, TableType } from '../../types'
import type { RootState } from '../index'

export const fetchTariffs = createAsyncThunk(
    'tariffs/fetchTariffs',
    async (_, { rejectWithValue }) => {
        try {
            return await tariffsApi.fetchTariffs()
        } catch (error: any) {
            return rejectWithValue(error.message)
        }
    }
)

export const fetchActiveTariffsByType = createAsyncThunk(
    'tariffs/fetchActiveTariffsByType',
    async (_, { rejectWithValue }) => {
        try {
            return await tariffsApi.fetchActiveTariffsByType()
        } catch (error: any) {
            return rejectWithValue(error.message)
        }
    }
)

export const fetchSessionTariffOptions = createAsyncThunk(
    'tariffs/fetchSessionTariffOptions',
    async (tableType: TableType, { rejectWithValue }) => {
        try {
            const options = await tariffsApi.fetchSessionTariffOptions(tableType)
            return { tableType, options }
        } catch (error: any) {
            return rejectWithValue(error.message)
        }
    }
)

export const createTariff = createAsyncThunk(
    'tariffs/createTariff',
    async (tariff: Omit<Tariff, 'id'>, { dispatch, rejectWithValue }) => {
        try {
            const newTariff = await tariffsApi.createTariff(tariff)
            await dispatch(fetchTariffs()).unwrap()
            await dispatch(fetchActiveTariffsByType()).unwrap()
            return newTariff
        } catch (error: any) {
            return rejectWithValue(error.message)
        }
    }
)

export const updateTariff = createAsyncThunk(
    'tariffs/updateTariff',
    async (
        { id, data }: { id: number; data: Partial<Tariff> },
        { dispatch, rejectWithValue }
    ) => {
        try {
            await tariffsApi.updateTariff(id, data)
            await dispatch(fetchTariffs()).unwrap()
            await dispatch(fetchActiveTariffsByType()).unwrap()
            return { id, data }
        } catch (error: any) {
            return rejectWithValue(error.message)
        }
    }
)

export const deleteTariff = createAsyncThunk(
    'tariffs/deleteTariff',
    async (id: number, { dispatch, rejectWithValue }) => {
        try {
            await tariffsApi.deleteTariff(id)
            await dispatch(fetchTariffs()).unwrap()
            await dispatch(fetchActiveTariffsByType()).unwrap()
            return id
        } catch (error: any) {
            return rejectWithValue(error.message)
        }
    }
)

/** Reorder: swap priority with the tariff above (higher priority). Fails if already first. */
export const reorderTariffMoveUp = createAsyncThunk(
    'tariffs/reorderTariffMoveUp',
    async (
        { tariffId, tableType }: { tariffId: number; tableType: TableType },
        { getState, dispatch, rejectWithValue }
    ) => {
        const state = getState() as RootState
        const list = state.tariffs.tariffs
            .filter((t) => t.tableType === tableType)
            .sort((a, b) => b.priority - a.priority)
        const idx = list.findIndex((t) => t.id === tariffId)
        if (idx <= 0) {
            return rejectWithValue('Tariff is already at the top')
        }
        const above = list[idx - 1]
        const selected = list[idx]
        try {
            await tariffsApi.updateTariff(above.id, { ...above, priority: selected.priority })
            await tariffsApi.updateTariff(selected.id, { ...selected, priority: above.priority })
            await dispatch(fetchTariffs()).unwrap()
            await dispatch(fetchActiveTariffsByType()).unwrap()
            return tariffId
        } catch (error: any) {
            return rejectWithValue(error?.message ?? 'Reorder failed')
        }
    }
)

/** Reorder: swap priority with the tariff below (lower priority). Fails if already last. */
export const reorderTariffMoveDown = createAsyncThunk(
    'tariffs/reorderTariffMoveDown',
    async (
        { tariffId, tableType }: { tariffId: number; tableType: TableType },
        { getState, dispatch, rejectWithValue }
    ) => {
        const state = getState() as RootState
        const list = state.tariffs.tariffs
            .filter((t) => t.tableType === tableType)
            .sort((a, b) => b.priority - a.priority)
        const idx = list.findIndex((t) => t.id === tariffId)
        if (idx < 0 || idx >= list.length - 1) {
            return rejectWithValue('Tariff is already at the bottom')
        }
        const below = list[idx + 1]
        const selected = list[idx]
        try {
            await tariffsApi.updateTariff(below.id, { ...below, priority: selected.priority })
            await tariffsApi.updateTariff(selected.id, { ...selected, priority: below.priority })
            await dispatch(fetchTariffs()).unwrap()
            await dispatch(fetchActiveTariffsByType()).unwrap()
            return tariffId
        } catch (error: any) {
            return rejectWithValue(error?.message ?? 'Reorder failed')
        }
    }
)